import { useEffect, useRef, useState } from 'react';

import { api, connectRunEvents, type RunEventsHandle } from '../services/api';
import type { RunEvent } from '../services/types';
import { useAppStore } from '../store/appStore';

/**
 * Subscribe to `/runs/:id/events` over WebSocket and:
 *
 * - Update the Zustand run cache with live status + progress
 * - Append each event to a local ring buffer for an on-screen log
 * - Clean up the socket on unmount
 *
 * The hook is inert if `runId` is null/undefined — useful when the caller
 * hasn't created a run yet.
 */
export function useRunEvents(runId: string | null | undefined, maxLog = 200) {
  const [events, setEvents] = useState<RunEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const updateRun = useAppStore((s) => s.updateRun);
  const bumpHitlVersion = useAppStore((s) => s.bumpHitlVersion);
  const handleRef = useRef<RunEventsHandle | null>(null);

  useEffect(() => {
    if (!runId) return;

    let cancelled = false;
    setConnected(true);

    // One-shot REST seed on subscribe.
    //
    // The WS push channel is the source of truth for live updates, but
    // there's a race: a fast pipeline (e.g. v0.4 Blob Handler short-
    // circuit on a JPEG upload) can terminate the run *before* the
    // browser's WS handshake completes, and the terminal `run_status`
    // event is broadcast to a subscriber that doesn't exist yet —
    // dropped on the floor. Without this seed, the UI sticks at the
    // last status the store happened to have at mount (typically the
    // "running" / progress=25% emitted just before the early-exit) and
    // the BlobHandlerErrorPanel conditional render never fires.
    //
    // Best-effort: a failure here just leaves the store as-is and the
    // WS subscriber takes over for any subsequent transitions.
    api
      .getRun(runId)
      .then((summary) => {
        if (cancelled) return;
        updateRun(runId, {
          status: summary.status,
          error_reason: summary.error_reason,
          run_result_status: summary.run_result_status,
          anomaly_count: summary.anomaly_count,
          schema_drift: summary.schema_drift,
          cross_field_flag_count: summary.cross_field_flag_count,
          column_policy_overrides: summary.column_policy_overrides,
          header_pii_columns: summary.header_pii_columns,
        });
      })
      .catch(() => {
        // Silent — the WS path remains; the user just won't see a
        // pre-existing terminal state until something else triggers
        // a refresh.
      });

    const handle = connectRunEvents(
      runId,
      (evt) => {
        if (cancelled) return;
        setEvents((prev) => {
          const next = prev.length >= maxLog ? prev.slice(1) : prev.slice();
          next.push(evt);
          return next;
        });

        // Mirror specific event types into the run cache.
        if (evt.type === 'progress') {
          updateRun(runId, { progressPct: evt.percent });
        } else if (evt.type === 'run_status') {
          updateRun(runId, { status: evt.status });
          // On error or complete, the WS event doesn't carry the
          // richer run-summary fields. Follow up with a single REST
          // fetch so the UI can surface error_reason (for crashes) and
          // run_result_status / anomaly_count (for partial successes)
          // without hunting through logs.
          if (evt.status === 'error' || evt.status === 'complete') {
            api
              .getRun(runId)
              .then((summary) => {
                if (cancelled) return;
                updateRun(runId, {
                  error_reason: summary.error_reason,
                  run_result_status: summary.run_result_status,
                  anomaly_count: summary.anomaly_count,
                  schema_drift: summary.schema_drift,
                  cross_field_flag_count: summary.cross_field_flag_count,
                  header_pii_columns: summary.header_pii_columns,
                });
              })
              .catch(() => {
                // Best-effort; a failed follow-up isn't worth a banner.
                // The UI degrades gracefully to the status alone.
              });
          }
        } else if (evt.type === 'hitl_created') {
          // Nudge the dashboard to refetch — the WS payload only carries
          // {hit_id, severity}, not the full item, so we can't splice it in
          // locally. `hitlVersion` is watched as a useEffect dep in
          // ReviewerDashboard for exactly this reason.
          bumpHitlVersion();
        }
      },
      () => {
        if (!cancelled) setConnected(false);
      },
    );
    handleRef.current = handle;

    return () => {
      cancelled = true;
      handle.close();
      handleRef.current = null;
      setConnected(false);
    };
  }, [runId, maxLog, updateRun, bumpHitlVersion]);

  return { events, connected };
}
